import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, Share2, Mail, Twitter, Facebook, 
  Copy, Check, Store, ShoppingBag, Sparkles, ArrowLeft,
  Building2, Users, Palette, Heart
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import ProductModal from "../components/ProductModal";
import { useLanguage, LanguageSwitcher } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const THEME_ICONS = {
  corporate: Building2,
  culture: Users,
  artistic: Palette,
  thematic: Heart,
  personality: Sparkles,
  lifestyle: ShoppingBag,
  mission: Heart,
  experience: Store
};

const THEME_COLORS = {
  corporate: "from-slate-600 to-slate-800",
  culture: "from-purple-600 to-pink-600",
  artistic: "from-rose-500 to-orange-500",
  thematic: "from-blue-600 to-cyan-500",
  personality: "from-violet-600 to-purple-600",
  lifestyle: "from-emerald-600 to-teal-600",
  mission: "from-red-600 to-rose-600",
  experience: "from-amber-600 to-orange-600"
};

export default function StorePreview() {
  const { storeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [store, setStore] = useState(location.state?.store || null);
  const [loading, setLoading] = useState(!location.state?.store);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [activeCollection, setActiveCollection] = useState(0);

  useEffect(() => {
    if (!store) {
      fetchStore();
    } else {
      showEmailPopupDelayed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const showEmailPopupDelayed = () => {
    const timer = setTimeout(() => {
      const dismissed = sessionStorage.getItem(`email-popup-${storeId}`);
      if (!dismissed) {
        setShowEmailPopup(true);
      }
    }, 2000);
    return () => clearTimeout(timer);
  };

  const fetchStore = async () => {
    try {
      const response = await axios.get(`${API}/store/${storeId}`);
      setStore(response.data);
      setLoading(false);
      showEmailPopupDelayed();
    } catch (error) {
      console.error("Failed to fetch store:", error);
      toast.error("Store not found");
      navigate("/");
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      await axios.post(`${API}/capture-email`, {
        email: email.trim(),
        store_id: storeId
      });
      setEmailSubmitted(true);
      toast.success("You're on the list!");
      setTimeout(() => {
        setShowEmailPopup(false);
        sessionStorage.setItem(`email-popup-${storeId}`, "true");
      }, 2000);
    } catch (error) {
      toast.error("Failed to submit email");
    }
  };

  const dismissEmailPopup = () => {
    setShowEmailPopup(false);
    sessionStorage.setItem(`email-popup-${storeId}`, "true");
  };

  const handleShare = async (platform) => {
    const shareUrl = window.location.href;
    const shareText = `Check out ${store?.brand?.name}'s merchandise!`;

    switch (platform) {
      case "copy":
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success("Link copied!");
        setTimeout(() => setCopied(false), 2000);
        break;
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
        break;
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");
        break;
      default:
        break;
    }
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (!store) return null;

  const { brand, category, collections } = store;
  const brandColors = brand?.colors || ["#0A0A0A", "#FFFFFF", "#7C3AED"];
  const primaryColor = brandColors[0] || "#0A0A0A";
  const hasMultipleCollections = collections && collections.length > 1;
  const currentCollection = collections?.[activeCollection] || collections?.[0];

  return (
    <div className="min-h-screen bg-white" data-testid="store-preview">
      {/* Success Banner */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 text-center"
      >
        <div className="flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-medium">{t('storeReady')}</span>
          <Sparkles className="w-5 h-5" />
        </div>
      </motion.div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{t('createNewStore')}</span>
          </button>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="gap-2"
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4" />
                {t('share')}
              </Button>
              
              <AnimatePresence>
                {showShareMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border py-2 z-50"
                  >
                    <button onClick={() => handleShare("copy")} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3">
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      Copy Link
                    </button>
                    <button onClick={() => handleShare("twitter")} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3">
                      <Twitter className="w-4 h-4" />
                      Share on X
                    </button>
                    <button onClick={() => handleShare("facebook")} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3">
                      <Facebook className="w-4 h-4" />
                      Share on Facebook
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Button size="sm" className="bg-black hover:bg-gray-800 text-white gap-2" data-testid="start-selling-button">
              <ShoppingBag className="w-4 h-4" />
              {t('startSelling')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="py-12 px-6"
        style={{ background: `linear-gradient(135deg, ${primaryColor}08 0%, white 50%, ${brandColors[2] || "#7C3AED"}08 100%)` }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Brand Identity */}
            <div className="flex flex-col items-center mb-6">
              {brand?.profile_image ? (
                <img src={brand.profile_image} alt={brand.name} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg mb-4" />
              ) : (
                <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4 shadow-lg" style={{ backgroundColor: primaryColor }}>
                  {brand?.name?.charAt(0) || "B"}
                </div>
              )}
              
              <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-1">{brand?.name}</h1>
              {brand?.username && <p className="text-gray-500 mb-2">@{brand.username}</p>}
              
              {/* Category Badge */}
              {category?.category_label && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm mb-3">
                  {category.category_label}
                </span>
              )}
              
              <p className="text-gray-600 max-w-lg text-sm">{brand?.tagline || brand?.bio?.slice(0, 100)}</p>
            </div>

            {/* Color Palette */}
            <div className="flex justify-center gap-2 mb-6">
              {brandColors.slice(0, 5).map((color, i) => (
                <div key={i} className="w-6 h-6 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: color }} title={color} />
              ))}
            </div>

            {/* Collection Info */}
            {hasMultipleCollections && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 max-w-2xl mx-auto mb-8"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-gray-900">{collections.length} {t('collectionsGenerated')}</span>
                </div>
                <p className="text-sm text-gray-600">
                  {t('detectedAs')} <strong>{category?.category_label}</strong>. 
                  {t('chooseCollection')}
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Collection Tabs */}
      {hasMultipleCollections ? (
        <section className="py-8 px-6 border-b border-gray-100">
          <div className="max-w-7xl mx-auto">
            <Tabs value={String(activeCollection)} onValueChange={(v) => setActiveCollection(Number(v))}>
              <TabsList className="grid w-full max-w-xl mx-auto" style={{ gridTemplateColumns: `repeat(${collections.length}, 1fr)` }}>
                {collections.map((collection, index) => {
                  const ThemeIcon = THEME_ICONS[collection.theme] || Store;
                  return (
                    <TabsTrigger 
                      key={index}
                      value={String(index)}
                      className="flex items-center gap-2 data-[state=active]:bg-black data-[state=active]:text-white"
                      data-testid={`collection-tab-${index}`}
                    >
                      <ThemeIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">{collection.theme_label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>
        </section>
      ) : null}

      {/* Current Collection Header */}
      {currentCollection && (
        <section className="py-8 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <motion.div
              key={activeCollection}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col md:flex-row items-center justify-between gap-4"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${THEME_COLORS[currentCollection.theme] || "from-gray-600 to-gray-800"} flex items-center justify-center`}>
                  {(() => {
                    const Icon = THEME_ICONS[currentCollection.theme] || Store;
                    return <Icon className="w-6 h-6 text-white" />;
                  })()}
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-gray-900">{currentCollection.theme_label}</h2>
                  <p className="text-sm text-gray-600">{currentCollection.theme_description}</p>
                </div>
              </div>
              <span className="text-sm text-gray-500">{currentCollection.products?.length || 9} {t('products')}</span>
            </motion.div>
          </div>
        </section>
      )}

      {/* Products Grid */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCollection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {currentCollection?.products?.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ProductCard 
                    product={product} 
                    onClick={() => setSelectedProduct(product)}
                    brandColors={brandColors}
                  />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-2xl font-semibold text-gray-900 mb-4">{t('readyToSell')}</h2>
          <p className="text-gray-600 mb-8">{t('readyToSellSubtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-black hover:bg-gray-800 text-white h-12 px-8" data-testid="contact-button">
              {t('requestChanges')}
            </Button>
            {hasMultipleCollections && (
              <Button variant="outline" className="h-12 px-8">
                {t('orderBoth')}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5" />
            <span className="font-semibold">InstaMerch</span>
          </div>
          <p className="text-sm text-gray-500">
            {t('poweredBy')} <a href="https://wiltee.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">Wiltee</a>
          </p>
        </div>
      </footer>

      {/* Email Capture Popup */}
      <Dialog open={showEmailPopup} onOpenChange={setShowEmailPopup}>
        <DialogContent className="sm:max-w-md" data-testid="email-popup">
          <button onClick={dismissEmailPopup} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
          
          <DialogHeader>
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-purple-600" />
            </div>
            <DialogTitle className="text-center text-xl">
              {emailSubmitted ? t('emailTitleSuccess') : t('emailTitle')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {emailSubmitted ? t('emailSubtitleSuccess') : t('emailSubtitle')}
            </DialogDescription>
          </DialogHeader>
          
          {!emailSubmitted && (
            <form onSubmit={handleEmailSubmit} className="mt-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1"
                  data-testid="email-input"
                  required
                />
                <Button type="submit" className="bg-black hover:bg-gray-800" data-testid="notify-button">
                  {t('notifyMe')}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <ProductModal 
        product={selectedProduct}
        brandColors={brandColors}
        brandName={brand?.name}
        onClose={() => setSelectedProduct(null)}
      />
    </div>
  );
}
